import {
  LazyModalKeysSettingsWalletLessLinkSuccess,
  LazyModalKeys,
  LazyModalKeysSettings,
  LazyModalRescue,
  LazyModalUserWalletCreate,
  LazyModalUserWalletLocked,
  ModalAuthLoader,
} from "#components";

export function useAuthController() {
  const userStore = useUserStore();
  const { $serverApi, $gfalIdApi } = useNuxtApp();

  const createAuthAttemptAndRedirect = async (
    authPath: string = "login",
    redirect: string = getCurrentFullPath(),
    rescue: boolean = false,
    forceMethod: "email" | "apple" | "facebook" | "google" | null = null
  ) => {
    const startTime = Date.now();
    useModalManager().openModal({
      id: "modal-loader",
      component: ModalAuthLoader,
    });
    let endpoint = "/oauth/authentications";
    if (rescue) endpoint = "/oauth/rescue/authentications";
    const referral = useCookie("referral").value;
    try {
      const requestAuth = async () => {
        try {
          return await $serverApi<GFALResponse<string | null>>(endpoint, {
            method: "POST",
            body: {
              authPath,
              redirect,
              referral,
            },
          });
        } catch {
          return await $fetch<GFALResponse<string | null>>(endpoint, {
            method: "POST",
            body: {
              authPath,
              redirect,
              referral,
            },
          });
        }
      };
      const [response] = await Promise.all([
        requestAuth(),
        new Promise((resolve) => {
          const elapsed = Date.now() - startTime;
          const remainingTime = Math.max(1000 - elapsed, 0);
          setTimeout(resolve, remainingTime);
        }),
      ]);

      if (response.statusCode === 200 && response.payload) {
        const url = new URL(response.payload);
        if (forceMethod) url.searchParams.set("startSocial", forceMethod);
        navigateTo(url.toString(), { external: true });
        // useModalManager().closeModal("modal-loader");
      } else {
        throw new Error("Failed to initialize OAuth login");
      }
    } catch (error) {
      useModalManager().closeModal("modal-loader");
      console.error("Failed to initialize OAuth login", error);
      return;
    }
  };

  const userLogin = async () => {
    const token = useCookie("token");
    if (!token.value) return Promise.resolve();
    try {
      const maintenancePromise = $serverApi<GFALResponse<any>>(
        "/api/status/maintenance",
        {
          headers: constructHeaders(token.value),
        }
      );
      const userPromise = $gfalIdApi<GFALResponse<GFALIDUser>>(
        "/api/users/self",
        {
          headers: constructHeaders(token.value),
        }
      );
      const userAPIPromise = $serverApi<GFALResponse<UserPayload>>(
        "/api/users/self",
        {
          headers: constructHeaders(token.value),
        }
      );

      const keysPromise = getKeys(token.value);
      const [maintenanceResponse, userResponse, userAPIResponse, keysResponse] =
        await Promise.all([
          maintenancePromise,
          userPromise,
          userAPIPromise,
          keysPromise,
        ]);
      if (!maintenanceResponse || maintenanceResponse.statusCode === 503)
        throw new Error("Maintenance");
      if (userResponse.statusCode !== 200 || !userResponse.payload)
        throw new Error("Failed to get user");
      if (userAPIResponse.statusCode === 200 && userAPIResponse.payload)
        userStore.setWallet(userAPIResponse.payload);
      userStore.authToken = token.value;
      userStore.setGfalIdUser(userResponse.payload);
      const isElementalRaidersPlayer = await getUserElementalRaidersAccount();
      userStore.elementalRaidersPlayer = isElementalRaidersPlayer;
    } catch (error) {
      console.error("Failed to login user", error);
      //userStore.logOut(); This implies client actions in the store that cannot be done in server
      userStore.$reset();
      throw error;
    }
  };

  const initClientLogin = async () => {
    if (!userStore.isLoggedIn) return;
    useGAnalytics().trackEvent("login_success");
    const rescueController = useRescueController();
    const registrationCallback =
      await rescueController.handlePublicKeyRegistrationCallback();
    rescueController.clearStalePublicKeyRegistrationIntent();
    const resumedWalletCreation =
      registrationCallback?.status === "success" &&
      registrationCallback.intent?.action === "create-wallet";
    const resumedWalletKeyUpdate =
      registrationCallback?.status === "success" &&
      registrationCallback.intent?.action === "update-wallet-keys";

    if (resumedWalletCreation) {
      await showWalletCreateModal({
        link: registrationCallback.intent?.link,
      });
    }
    if (resumedWalletKeyUpdate && registrationCallback.intent) {
      useModalManager().openModal({
        id: registrationCallback.intent.modal,
        component:
          registrationCallback.intent.modal === "keys"
            ? LazyModalKeys
            : LazyModalKeysSettings,
        props: { type: registrationCallback.intent.type },
      });
    }

    continueWalletlessLinkWithSocial();
    if (!resumedWalletCreation) continueWalletCreationWithSocial();
    continueWalletUnlockWithSocial();
    continueRescueWithSocial();
    handlePayloadAndWid();
    useNotifications().fetchNotifications();
    usePolling().initRateRefresh();
    if (
      userStore.requireWalletUpdate &&
      !resumedWalletCreation &&
      !resumedWalletKeyUpdate
    )
      await showFinalWalletModal();
  };

  const getUserElementalRaidersAccount = async () => {
    try {
      const response = await $serverApi<GFALResponse<any>>(
        "/api/merchants/elemental-raiders/user",
        {
          headers: constructHeaders(userStore.authToken),
        }
      );
    } catch (error: any) {
      switch (error.statusCode) {
        case 404:
          return false;
        default:
          console.error("error", error);
          return true;
      }
    }
    return true;
  };

  const showFinalWalletModal = async () => {
    if (useRoute().path === "/rescue") return;
    (await shouldShowWalletCreateModal())
      ? showWalletCreateModal()
      : showWalletLockedModal();
  };

  const shouldShowWalletCreateModal = async () => {
    try {
      await useWeb3().getPrivateKey();
      return true;
    } catch {
      return false;
    }
  };

  const handlePayloadAndWid = async () => {
    if (useRoute().path === "/rescue") return;
    if (!userStore.accountAddress || userStore.requireWalletUpdate) return;
    const wid = useCookie(`wid-${userStore.id}`);
    if (wid.value) return;
    const gfalId = useCookie(`gfalid-${userStore.id}`);
    if (gfalId.value && !useRoute().query["existing-link"]) {
      await decryptAndSetPrivateKey(gfalId.value)
        .then(() => {
          gfalId.value = null;
        })
        .catch((error) => {
          console.error("decryptAndSetPrivateKey error:", error);
        });
    }
  };

  const decryptAndSetPrivateKey = async (cookieString: string) => {
    const stringifiedPayload = await deobfuscate(cookieString);
    const { type, value } = JSON.parse(stringifiedPayload) as {
      type: keyof typeof userStore.keys;
      value: string;
    };
    if (!userStore.keys) return;
    if (!(type in userStore.keys) || userStore.keys[type] === null) {
      if (
        type === "password" ||
        type === "google" ||
        type === "apple" ||
        type === "facebook"
      ) {
        userStore.walletLocked = true;
        showWalletLockedModal();
      }
      throw new Error("Key type not found in user keys");
    }
    const widKey = (userStore.keys[type] as { value: string }).value;
    if (!widKey) throw new Error("No WID key found");
    let privateKey: string;
    try {
      privateKey = await decrypt(widKey, value);
    } catch (error: any) {
      if (useRoute().path === "/rescue") return;
      if (type === "password") {
        console.log("Failed to decrypt login key", error);
        userStore.walletLocked = true;
        showWalletLockedModal();
      }
      if (
        (type === "google" || type === "apple" || type === "facebook") &&
        userStore.keys.recoveryPassword !== null
      ) {
        console.log("Failed to decrypt social key", error);
        userStore.walletLocked = true;
        showWalletLockedModal();
      } else {
        showWalletLockedModalSupport();
      }
      throw error;
    }
    useWeb3().setPrivateKey(privateKey);
  };

  const request2FA = async () => {
    try {
      const response = await $gfalIdApi<GFALResponse<null>>("/api/2fa", {
        method: "POST",
        headers: constructHeaders(userStore.authToken),
      });
      return response;
    } catch (error) {
      console.error("Failed to request 2FA", error);
      return null;
    }
  };

  const toggle2FA = async (twoFactorAuth: boolean, code?: string) => {
    try {
      const response = await $gfalIdApi<GFALResponse<null>>(
        "/api/users/self/2fa",
        {
          method: "PUT",
          body: {
            twoFactorAuth,
            code,
          },
          headers: constructHeaders(userStore.authToken),
        }
      );
      return response;
    } catch (error: any) {
      if (error.statusCode === 400) {
        return error;
      } else if (error.statusCode === 403) {
        return error;
      }
      console.error("Failed to toggle 2FA", error);
      return null;
    }
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ) => {
    try {
      const response = await $gfalIdApi<GFALResponse<null>>(
        "/api/users/self/password",
        {
          method: "PUT",
          body: {
            currentPassword,
            newPassword,
          },
          headers: constructHeaders(userStore.authToken),
        }
      );
      return response;
    } catch (error: any) {
      if (error.statusCode === 403) {
        return error;
      }
      if (error.statusCode === 412) {
        return error;
      }
      console.error("Failed to change password", error);
      return null;
    }
  };

  const verifyEmail = async (reCaptchaToken: string, key: string) => {
    try {
      const response = await $gfalIdApi<GFALResponse<null>>(
        "/api/keys/verify-email",
        {
          method: "POST",
          body: {
            key,
          },
          headers: {
            "X-Humanity-Token": reCaptchaToken,
          },
        }
      );
      return response;
    } catch (error) {
      console.error("Failed to verify email", error);
      return null;
    }
  };

  const showWalletCreateModal = async (props?: { link?: boolean }) => {
    if (useRoute().path === "/rescue") return;
    useModalManager().openModal({
      id: "modal-user-wallet-create",
      component: LazyModalUserWalletCreate,
      props,
    });
  };

  const showWalletLockedModal = async () => {
    useModalManager().openModal({
      id: "modal-user-wallet-locked",
      component: LazyModalUserWalletLocked,
    });
  };

  const showWalletLockedModalSupport = async () => {
    useModalManager().openModal({
      id: "modal-user-wallet-locked",
      component: LazyModalUserWalletLocked,
      props: {
        support: true,
      },
    });
  };

  const continueWalletCreationWithSocial = async () => {
    const route = useRoute();
    if (route.query.link || route.query["new-wallet-existing-link"])
      useModalManager().openModal({
        id: "modal-user-wallet-create",
        component: LazyModalUserWalletCreate,
        props: {
          link: true,
        },
      });
  };

  const continueRescueWithSocial = async () => {
    const route = useRoute();
    if (route.query.rescue)
      useModalManager().openModal({
        id: "modal-rescue",
        component: LazyModalRescue,
      });
  };

  const continueWalletlessLinkWithSocial = async () => {
    const route = useRoute();
    if (route.query["walletless-link"])
      useModalManager().openModal({
        id: "modal-keys-settings-walletless-link-success",
        component: LazyModalKeysSettingsWalletLessLinkSuccess,
        props: {
          link: true,
        },
      });
  };

  const continueWalletUnlockWithSocial = async () => {
    const route = useRoute();
    if (route.query["existing-link"])
      useModalManager().openModal({
        id: "modal-user-wallet-locked",
        component: LazyModalUserWalletLocked,
      });
  };

  const getKeys = async (token: string) => {
    try {
      const response = await $fetch("/api/vault/keys", {
        headers: constructHeaders(token),
      });
      if (response.statusCode === 200 && response.payload) {
        userStore.setKeys(response.payload);
        requiresWalletUpdate(response.payload);
        return response.payload;
      }
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return null;
      } else {
        throw new Error("Failed to get keys");
      }
    }
  };

  const requiresWalletUpdate = (keys: UserKeys) => {
    if (keys.privateKey) {
      userStore.requireWalletUpdate = true;
      return;
    }
    if (
      !keys.password &&
      !keys.google &&
      !keys.apple &&
      !keys.facebook &&
      !keys.recoveryPassword
    ) {
      if (keys.rescue) {
        userStore.requireWalletUpdate = true;
      } else {
        throw new Error("No keys found");
      }
    }
  };

  const updateKeys = async (keys: UserKeys) => {
    const response = await $fetch("/api/vault/keys", {
      method: "PUT",
      body: keys,
      headers: constructHeaders(userStore.authToken),
    });
    if (response.statusCode === 200 && response.payload)
      userStore.setKeys(response.payload);
    return response;
  };

  return {
    createAuthAttemptAndRedirect,
    userLogin,
    handlePayloadAndWid,
    decryptAndSetPrivateKey,
    request2FA,
    toggle2FA,
    changePassword,
    verifyEmail,
    updateKeys,
    initClientLogin,
  };
}
